<div class="editor-loading" *ngIf="loading">Loading workflow…</div>

<div class="editor-load-error" *ngIf="!loading && loadError">
  <br-icon>error_outline</br-icon>
  <p>{{ loadError }}</p>
  <div class="editor-load-error-actions">
    <button type="button" class="bar-btn" (click)="back()">Back</button>
    <button type="button" class="bar-primary" (click)="reload()">Retry</button>
  </div>
</div>

<div class="editor" *ngIf="!loading && !loadError">
  <header class="editor-bar">
    <label class="bar-label" for="workflow-name">
      Workflow name <span class="br-required-mark" aria-hidden="true">*</span>
    </label>
    <input
        id="workflow-name"
        class="bar-name"
        [(ngModel)]="draft.label"
        (change)="editStep()"
        [readonly]="readOnly"
        required
        maxlength="128"
        aria-label="Workflow name"
        aria-required="true"
        placeholder="Workflow name" />

    <span class="bar-spacer"></span>

    <button
        *ngIf="!readOnly"
        type="button"
        class="bar-btn"
        [disabled]="!draft.nodes.length || graph?.interactionActive"
        (click)="applyAutoLayout()">
      Auto layout
    </button>

    <span class="bar-history" *ngIf="!readOnly">
      <button type="button" class="bar-icon" aria-label="Undo" title="Undo"
              [disabled]="!canUndo" (click)="undo()">↺</button>
      <button type="button" class="bar-icon" aria-label="Redo" title="Redo"
              [disabled]="!canRedo" (click)="redo()">↻</button>
    </span>

    <button
        type="button"
        class="bar-btn"
        (click)="back()">
      Back
    </button>

    <button
        *ngIf="!readOnly"
        type="button"
        class="bar-primary"
        [disabled]="saving"
        (click)="save()">
      {{ saving ? 'Saving…' : 'Save draft' }}
    </button>

    <button
        *ngIf="status === 'draft'"
        type="button"
        class="bar-primary"
        [disabled]="!draft.nodes.length || validation.errorCount > 0 || dirty"
        [title]="dirty
          ? 'Save your changes first'
          : (validation.errorCount ? 'Fix the errors first' : 'Publish this workflow')"
        (click)="publish()">
      Publish
    </button>

    <span class="bar-locked" *ngIf="readOnly">
      <br-icon>lock</br-icon> Published — read only
    </span>

    <button
        *ngIf="readOnly"
        type="button"
        class="bar-btn"
        [disabled]="saving"
        (click)="unpublish()">
      {{ saving ? 'Unpublishing…' : 'Unpublish' }}
    </button>
  </header>

  <div class="editor-body">
    <app-dynamic-workflow-graph
        [nodes]="draft.nodes"
        [transitions]="draft.transitions"
        [layout]="layout"
        [readOnly]="readOnly"
        [selectedNodeKey]="selectedNodeKey"
        [selectedTransitionIndex]="selectedTransitionIndex"
        [errorNodeKeys]="errorNodeKeys"
        (nodeSelected)="selectNodeByKey($event)"
        (transitionSelected)="selectTransition($event)"
        (selectionCleared)="clearSelection()"
        (nodeMoved)="onNodeMoved($event)"
        (transitionCreated)="onTransitionCreated($event)"
        (transitionReconnected)="onTransitionReconnected($event)" />


    <!-- The workflow as a tree. Nodes and transitions are siblings because a
         field belongs to one node but a transition belongs to two, so nesting
         transitions under a node would either duplicate them or pick a side.
         Keeping them level also means merges and loops need no special case. -->
    <aside class="nodes-panel">
      <div class="tree-alerts" *ngIf="workflowIssues.length">
        <p
            class="tree-alert"
            *ngFor="let issue of workflowIssues"
            [class.is-warn]="issue.severity === 'warning'">
          <span class="alert-dot"></span>{{ issue.message }}
        </p>
      </div>

      <div class="nodes-panel-scroll">
        <section class="tree-group">
          <div class="group-head">
            <button
                type="button"
                class="group-toggle"
                [attr.aria-expanded]="nodesOpen"
                (click)="nodesOpen = !nodesOpen">
              <br-icon>{{ nodesOpen ? 'expand_more' : 'chevron_right' }}</br-icon>
              <span>Nodes</span>
              <span class="group-count">{{ draft.nodes.length }}</span>
            </button>
            <button
                *ngIf="!readOnly"
                type="button"
                class="group-add"
                title="Add node"
                aria-label="Add node"
                (click)="addNode()">
              <br-icon>add</br-icon>
            </button>
          </div>

          <div class="group-body" *ngIf="nodesOpen">
            <p class="child-empty" *ngIf="!draft.nodes.length">
              A node is one step a record passes through.
            </p>

            <ng-container *ngFor="let node of draft.nodes; let nodeIndex = index">
              <div
                  class="tree-row"
                  [class.is-open]="selectedNodeIndex === nodeIndex"
                  [class.has-error]="errorNodeKeys.has(node.key)">
                <button
                    type="button"
                    class="row-main"
                    [attr.aria-expanded]="selectedNodeIndex === nodeIndex"
                    (click)="toggleNode(nodeIndex)">
                  <br-icon class="row-twisty">
                    {{ selectedNodeIndex === nodeIndex ? 'expand_more' : 'chevron_right' }}
                  </br-icon>
                  <span class="row-dot" [style.background]="node.color || '#8a909c'"></span>
                  <span class="row-name">{{ node.label }}</span>
                </button>
                <button
                    *ngIf="selectedNodeIndex === nodeIndex && !readOnly"
                    type="button"
                    class="row-icon row-delete"
                    title="Remove node"
                    [attr.aria-label]="'Remove ' + node.label"
                    (click)="removeNode(nodeIndex)">
                  <br-icon>delete_outline</br-icon>
                </button>
              </div>

              <div class="row-children" *ngIf="selectedNodeIndex === nodeIndex">
                <p
                    class="row-issue"
                    *ngFor="let issue of issuesFor(node.key)"
                    [class.is-warn]="issue.severity === 'warning'">
                  {{ issue.message }}
                </p>

                <div class="node-properties">
                  <label class="form-label" for="node-name-{{ nodeIndex }}">
                    Name <span class="br-required-mark" aria-hidden="true">*</span>
                  </label>
                  <input
                      id="node-name-{{ nodeIndex }}"
                      class="field-input"
                      [(ngModel)]="node.label"
                      (change)="editStep()"
                      [readonly]="readOnly"
                      required
                      aria-required="true"
                      maxlength="128" />

                  <span class="form-label">Colour</span>
                  <div class="swatches">
                    <button
                        *ngFor="let color of colors"
                        type="button"
                        class="swatch"
                        [class.is-selected]="node.color === color"
                        [style.background]="color"
                        [disabled]="readOnly"
                        [attr.aria-label]="'Use colour ' + color"
                        (click)="setColor(node, color)"></button>
                  </div>
                </div>

                <div class="child-head">
                  <span>Fields</span>
                  <button
                      *ngIf="!readOnly"
                      type="button"
                      class="child-add"
                      title="Add field"
                      aria-label="Add field"
                      (click)="addField(node)">
                    <br-icon>add</br-icon>
                  </button>
                </div>

                <div class="tree-row is-child" *ngFor="let item of node.fields; let fieldIndex = index">
                  <button
                      type="button"
                      class="row-main"
                      [disabled]="readOnly"
                      (click)="editField(node, fieldIndex)">
                    <span class="row-name">
                      {{ item.label }}<span
                          *ngIf="item.isRequired"
                          class="br-required-mark"
                          aria-label="Required">*</span>
                    </span>
                    <span class="row-meta">{{ fieldTypeLabel(item) }}</span>
                  </button>
                  <button
                      type="button"
                      class="row-icon"
                      [disabled]="readOnly"
                      title="Remove field"
                      [attr.aria-label]="'Remove ' + item.label"
                      (click)="removeField(node, fieldIndex)">
                    <br-icon>close</br-icon>
                  </button>
                </div>

              </div>
            </ng-container>
          </div>
        </section>

        <section class="tree-group">
          <div class="group-head">
            <button
                type="button"
                class="group-toggle"
                [attr.aria-expanded]="transitionsOpen"
                (click)="transitionsOpen = !transitionsOpen">
              <br-icon>{{ transitionsOpen ? 'expand_more' : 'chevron_right' }}</br-icon>
              <span>Transitions</span>
              <span class="group-count">{{ draft.transitions.length }}</span>
            </button>
            <button
                *ngIf="!readOnly"
                type="button"
                class="group-add"
                title="Add transition"
                aria-label="Add transition"
                [disabled]="draft.nodes.length < 2"
                (click)="addTransition()">
              <br-icon>add</br-icon>
            </button>
          </div>

          <div class="group-body" *ngIf="transitionsOpen">
            <ng-container *ngFor="let transition of draft.transitions; let edgeIndex = index">
              <div class="tree-row" [class.is-open]="selectedTransitionIndex === edgeIndex">
                <button
                    type="button"
                    class="row-main"
                    [attr.aria-expanded]="selectedTransitionIndex === edgeIndex"
                    (click)="toggleTransition(edgeIndex)">
                  <br-icon class="row-twisty">
                    {{ selectedTransitionIndex === edgeIndex ? 'expand_more' : 'chevron_right' }}
                  </br-icon>
                  <span class="row-name">{{ nodeLabel(transition.fromNodeKey) }}</span>
                  <span class="row-arrow">&rarr;</span>
                  <span class="row-name">{{ nodeLabel(transition.toNodeKey) }}</span>
                </button>
                <button
                    *ngIf="selectedTransitionIndex === edgeIndex && !readOnly"
                    type="button"
                    class="row-icon row-delete"
                    title="Remove transition"
                    aria-label="Remove transition"
                    (click)="removeTransition(edgeIndex)">
                  <br-icon>delete_outline</br-icon>
                </button>
              </div>

              <div class="row-children" *ngIf="selectedTransitionIndex === edgeIndex">
                <div class="transition-route">
                  <label for="transition-from-{{ edgeIndex }}">
                    From <span class="br-required-mark" aria-hidden="true">*</span>
                  </label>
                  <select class="field-input" [(ngModel)]="transition.fromNodeKey"
                          id="transition-from-{{ edgeIndex }}"
                          [title]="nodeLabel(transition.fromNodeKey)"
                          required
                          aria-required="true"
                          (change)="editStep()" [disabled]="readOnly">
                    <option *ngFor="let node of draft.nodes" [value]="node.key">{{ node.label }}</option>
                  </select>

                  <label for="transition-to-{{ edgeIndex }}">
                    To <span class="br-required-mark" aria-hidden="true">*</span>
                  </label>
                  <select class="field-input" [(ngModel)]="transition.toNodeKey"
                          id="transition-to-{{ edgeIndex }}"
                          [title]="nodeLabel(transition.toNodeKey)"
                          required
                          aria-required="true"
                          (change)="editStep()" [disabled]="readOnly">
                    <option *ngFor="let node of draft.nodes" [value]="node.key">{{ node.label }}</option>
                  </select>
                </div>

              </div>
            </ng-container>
          </div>
        </section>
      </div>
    </aside>
  </div>
</div>
